import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { xsrfToken } from '@/api/session';
import type { Schema } from '@/api/types';
import { Popover } from '@/components/orno/popover';

function initials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase());
  return letters.join('') || '?';
}

/** The signed-in person's menu: who they are, and Sign out (a form post with the CSRF token, as the backend wants). */
export function UserMenu({ user }: { user: Schema<'Me'> }) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const csrfRef = useRef<HTMLInputElement>(null);

  // The token is read when signing out, not when rendering: the backend may have rotated it since.
  const signOut = () => {
    if (csrfRef.current) csrfRef.current.value = xsrfToken() ?? '';
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Popover
        triggerLabel={t('account.menu')}
        triggerClassName="btn-ghost user-menu__trigger"
        trigger={
          <span className="avatar avatar--sm" aria-hidden="true">
            {initials(user.name)}
          </span>
        }
        placement="bottom-end"
      >
        <div role="group" aria-label={t('account.menu')} className="user-menu">
          <div className="user-menu__who">
            <span className="b">{user.name}</span>
            <span className="note">{user.email}</span>
          </div>
          <hr className="sep user-menu__sep" />
          <button type="button" className="opt" onClick={signOut}>
            {t('account.signOut')}
          </button>
        </div>
      </Popover>
      <form method="post" action="/api/logout" ref={formRef} hidden>
        <input type="hidden" name="_csrf" defaultValue={xsrfToken() ?? ''} ref={csrfRef} />
      </form>
    </>
  );
}
